import { Box, Chip, Grid, Typography } from '@mui/material'
import { isUndefined, keys } from 'lodash-es'
import { useFormat } from 'Services/localize/format'
import { OrderItemViewModel } from 'store/models/sellModels'
import { CartModifierRow } from './CartModifierRow'
import { CartVariantOptionRow } from './CartVariantOptionRow'
import { CartComboRow } from './CartComboRow'
import { OrderLineKdsStatus, OrderLineStatus, OrderPaymentStatus } from 'models'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Pending from '@mui/icons-material/Pending'
import Save from '@mui/icons-material/Save'
import { KitchenStatusIcon } from 'components/Sell/TableLookupDialog/components/KitchenStatusManual'
import { useTranslation } from 'react-i18next'
import useTableLayoutDisplay from 'hooks/useTableLayoutDisplay/useTableLayoutDisplay'

interface IProps {
    item: OrderItemViewModel
    isReadonly?: boolean
}

export default function CartItemDetail({ item, isReadonly = false }: IProps) {
    return (
        <>
            {keys(item.usedItems).map((path) => {
                return (
                    <CartComboRow
                        key={`combo-choice-${path}`}
                        isReadonly={isReadonly}
                        lineId={item.id}
                        comboItemId={item.item.itemId!}
                        comboChoiceId={path.split(',')[0]!}
                        usedItem={item.usedItems[path]}
                        menuId={item.menuId!}
                        itemQuantity={item.amount}
                        isOrderLineVoided={
                            item.status === OrderLineStatus.Voided
                        }
                    />
                )
            })}
            <CartVariantOptionRow
                usedVariants={item.usedVariants}
                isOrderLineVoided={item.status === OrderLineStatus.Voided}
            />
            {keys(item.usedModifiers).map((mo) => {
                return (
                    <CartModifierRow
                        key={`modifier-option-row-${mo}-${item.id}`}
                        lineId={item.id}
                        menuId={item.menuId}
                        usedModifiers={item.usedModifiers[mo]}
                        bindingId={mo}
                        itemQuantity={item.amount}
                        isOrderLinedVoided={
                            item.status === OrderLineStatus.Voided
                        }
                    />
                )
            })}
        </>
    )
}
interface DisplayLineProps {
    isDeduct?: boolean | undefined
    name: string
    price?: number
    quantity?: number
    isActive?: boolean
    priceWithDiscount?: number
    isSharedLine?: boolean
    sharedStatus?: OrderPaymentStatus
    isBaseItem?: boolean
    orderLineStatus?: OrderLineStatus
    kdsStatus?: OrderLineKdsStatus
    isNotParticipated?: boolean
    itemDiscountAmount?: number
    hidePrice?: boolean
    seatNote?: string | null
}
export const DisplayLine = ({
    isDeduct,
    name,
    price,
    quantity,
    isActive = true,
    priceWithDiscount,
    isSharedLine = false,
    sharedStatus = OrderPaymentStatus.Pending,
    isBaseItem = false,
    kdsStatus,
    isNotParticipated = false,
    itemDiscountAmount = 0,
    hidePrice = false,
    seatNote,
}: DisplayLineProps): JSX.Element => {
    const { currency } = useFormat()
    const { t } = useTranslation()
    const { displaySeatNote } = useTableLayoutDisplay()

    function ItemName() {
        return (
            <Typography
                component={'span'}
                sx={{
                    textDecoration: isActive ? undefined : 'line-through',
                }}
            >
                {name}
                <KitchenStatusIcon
                    kdsStatus={kdsStatus}
                    sx={{ mb: '-3px', pl: 0.5 }}
                />
                {isNotParticipated && (
                    <Typography variant="caption" color="disabled.contrastText">
                        {t(
                            'table_sale_lookup.kitchen_status.status.not_related',
                        )}
                    </Typography>
                )}
            </Typography>
        )
    }
    return (
        <Grid container justifyContent={'space-between'}>
            {(!isUndefined(isDeduct) || !isUndefined(quantity)) && (
                <Grid
                    item
                    xs={isBaseItem ? 2 : 'auto'}
                    lg={isBaseItem ? 1 : 'auto'}
                    mr={isBaseItem ? 0 : 1}
                >
                    <Typography>
                        {!isUndefined(isDeduct) && (isDeduct ? '- ' : '+ ')}
                        {!isUndefined(quantity) && !isDeduct && `${quantity}x`}
                    </Typography>
                </Grid>
            )}
            <Grid item xs>
                <Typography
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                    component="div"
                >
                    <Box component={'span'}>
                        <ItemName />
                        {seatNote ? (
                            <Chip
                                sx={{ ml: 0.5 }}
                                size="small"
                                label={displaySeatNote(seatNote)}
                                color="primary"
                            />
                        ) : null}
                    </Box>
                    {((price && price !== 0) || isSharedLine) && (
                        <Box component={'span'}>
                            {price && price !== 0 ? (
                                <>
                                    {!hidePrice && (
                                        <>
                                            <Typography
                                                color={
                                                    (priceWithDiscount ??
                                                        price) < 0
                                                        ? 'error.main'
                                                        : 'text.primary'
                                                }
                                                component={'span'}
                                            >
                                                {currency(
                                                    priceWithDiscount ?? price,
                                                )}
                                            </Typography>{' '}
                                            {itemDiscountAmount !== 0 && (
                                                <Typography
                                                    component={'span'}
                                                    sx={{
                                                        textDecoration:
                                                            'line-through',
                                                        ml: '5px',
                                                    }}
                                                >
                                                    {currency(price)}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                // isSharedLine case
                                <Typography
                                    color={
                                        sharedStatus === OrderPaymentStatus.Paid
                                            ? 'primary.main'
                                            : sharedStatus ===
                                              OrderPaymentStatus.PartiallyPaid
                                            ? 'warning.main'
                                            : 'bluegrey.light'
                                    }
                                >
                                    Shared{' '}
                                    {sharedStatus ===
                                    OrderPaymentStatus.Paid ? (
                                        <CheckCircle
                                            sx={{ mb: '-3px' }}
                                            fontSize="small"
                                        />
                                    ) : sharedStatus ===
                                      OrderPaymentStatus.PartiallyPaid ? (
                                        <Pending
                                            sx={{ mb: '-3px' }}
                                            fontSize="small"
                                        />
                                    ) : (
                                        <Save
                                            sx={{ mb: '-3px' }}
                                            fontSize="small"
                                        />
                                    )}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Typography>
            </Grid>
        </Grid>
    )
}
